import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BackButton } from '../components/NavButtons';
import { Button } from '../components/ui/button';
import axios from 'axios';
import {
  VideoCamera, PhoneDisconnect, Microphone, MicrophoneSlash,
  Camera as CameraIcon, CameraSlash, Users, ArrowSquareOut, Copy,
  HandHeart, Clock, MapPin as MapPinIcon
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VideoRoom = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [joinInfo, setJoinInfo] = useState(null);
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [jitsiReady, setJitsiReady] = useState(false);
  const [studio, setStudio] = useState(null); // seller-only studio settings
  const jitsiContainer = useRef(null);
  const jitsiApi = useRef(null);

  // Determine if the current user is the host (seller of this class)
  const isHost = !!(classInfo && user && classInfo.seller_id === user.user_id);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [joinRes, classRes] = await Promise.all([
          axios.get(`${API_URL}/api/live-classes/${classId}/join`),
          axios.get(`${API_URL}/api/live-classes/${classId}`),
        ]);
        setJoinInfo(joinRes.data);
        setClassInfo(classRes.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Unable to join. You may not be booked for this class.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [classId]);

  // Fetch seller's studio settings if user might be the host (we don't yet know — fetch defensively)
  useEffect(() => {
    if (!user || !['seller', 'both', 'admin'].includes(user.role)) return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/studio/settings`);
        setStudio(res.data || null);
      } catch (e) {
        // 404 / 401 = not a seller or no settings yet → silently ignore. Anything else worth logging.
        const status = e?.response?.status;
        if (status && status >= 500) {
          console.warn('Studio settings fetch failed:', status, e?.message);
        }
      }
    })();
  }, [user]);

  // Load Jitsi script
  useEffect(() => {
    if (!joinInfo || joinInfo.meeting_type !== 'jitsi') return;
    if (document.getElementById('jitsi-script')) {
      setJitsiReady(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'jitsi-script';
    script.src = 'https://meet.jit.si/external_api.js';
    script.async = true;
    script.onload = () => setJitsiReady(true);
    document.head.appendChild(script);
  }, [joinInfo]);

  // Initialize Jitsi Meet
  useEffect(() => {
    if (!jitsiReady || !joinInfo || joinInfo.meeting_type !== 'jitsi' || !jitsiContainer.current) return;
    if (jitsiApi.current) return;

    const domain = 'meet.jit.si';
    const options = {
      roomName: joinInfo.jitsi_room,
      width: '100%',
      height: '100%',
      parentNode: jitsiContainer.current,
      userInfo: {
        displayName: joinInfo.user_name || 'Participant',
      },
      configOverwrite: {
        startWithAudioMuted: false,
        startWithVideoMuted: false,
        prejoinPageEnabled: false,
        disableDeepLinking: true,
        // Studio enhancements (host-only studio settings apply automatically)
        toolbarButtons: [
          'microphone', 'camera', 'desktop', 'fullscreen', 'hangup',
          'chat', 'raisehand', 'tileview', 'participants-pane',
          'select-background', 'noisesuppression', 'videoquality',
        ],
        // Enable noise suppression by default if seller has it in their studio prefs (or default true for hosts)
        ...(isHost && studio?.noise_suppression !== false ? { enableNoisyMicDetection: true } : {}),
      },
      interfaceConfigOverwrite: {
        SHOW_JITSI_WATERMARK: false,
        SHOW_WATERMARK_FOR_GUESTS: false,
        DEFAULT_BACKGROUND: '#0f1219',
        TOOLBAR_ALWAYS_VISIBLE: true,
        DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
        FILM_STRIP_MAX_HEIGHT: 120,
      },
    };

    try {
      const api = new window.JitsiMeetExternalAPI(domain, options);
      jitsiApi.current = api;
      api.addListener('readyToClose', () => {
        navigate(`/live-classes`);
      });
      // After conference joined, apply studio settings for the host
      api.addListener('videoConferenceJoined', () => {
        if (!isHost || !studio) return;
        try {
          // Background
          if (studio.background_type === 'blur') {
            api.executeCommand('setVirtualBackground', { backgroundType: 'blur', blurValue: 25 });
          } else if (studio.background_type === 'image' || studio.background_type === 'ai') {
            const url = studio.background_url?.startsWith('http')
              ? studio.background_url
              : `${API_URL}${studio.background_url || ''}`;
            if (url) {
              api.executeCommand('setVirtualBackground', { backgroundType: 'image', virtualSource: url });
            }
          }
          // Noise suppression (host)
          if (studio.noise_suppression) {
            try { api.executeCommand('toggleNoiseSuppression'); }
            catch (toggleErr) { console.warn('Noise suppression toggle unavailable in this Jitsi version:', toggleErr?.message); }
          }
        } catch (e) { console.warn('Studio apply failed:', e?.message); }
      });
    } catch (e) {
      console.error('Jitsi init error:', e);
      setError('Failed to initialize video room');
    }

    return () => {
      if (jitsiApi.current) {
        jitsiApi.current.dispose();
        jitsiApi.current = null;
      }
    };
  }, [jitsiReady, joinInfo, navigate, isHost, studio]);

  const handleLeave = () => {
    if (jitsiApi.current) {
      jitsiApi.current.executeCommand('hangup');
    }
    navigate('/live-classes');
  };

  const copyMeetingLink = () => {
    const link = joinInfo?.meeting_type === 'external'
      ? joinInfo.external_meeting_link
      : `https://meet.jit.si/${joinInfo?.jitsi_room}`;
    navigator.clipboard.writeText(link);
    toast.success('Meeting link copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f1219] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Connecting to class...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0f1219] flex items-center justify-center p-4">
        <div className="bg-[#1a2033] rounded-2xl p-8 text-center max-w-md border border-[#252d3d]">
          <VideoCamera size={48} className="text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-white mb-2">Unable to Join</h2>
          <p className="text-slate-400 text-sm mb-4">{error}</p>
          <Button onClick={() => navigate('/live-classes')} className="bg-pink-500 hover:bg-pink-600 rounded-xl">
            Back to Classes
          </Button>
        </div>
      </div>
    );
  }

  // In-person session view — show the venue instead of a meeting room
  if (joinInfo?.meeting_type === 'in_person') {
    return (
      <div className="min-h-screen bg-[#0f1219] flex items-center justify-center p-4">
        <div className="bg-[#1a2033] rounded-2xl p-8 text-center max-w-md border border-[#252d3d]" data-testid="in-person-view">
          <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <MapPinIcon size={32} className="text-amber-400" weight="fill" />
          </div>
          <h2 className="text-xl font-bold text-white mb-1">{classInfo?.title}</h2>
          <p className="text-slate-400 text-sm mb-1">{classInfo?.instructor && `by ${classInfo.instructor}`}</p>
          <p className="text-slate-500 text-xs mb-6 flex items-center justify-center gap-2">
            <Clock size={14} /> {classInfo?.date} | {classInfo?.start_time} - {classInfo?.end_time}
          </p>
          <p className="text-slate-400 text-xs mb-2">This is an in-person session. Please arrive at the venue:</p>
          <p className="text-white text-sm font-semibold bg-[#0f1219] border border-[#252d3d] rounded-xl p-4 mb-6" data-testid="in-person-venue">
            {joinInfo.venue_address || 'Venue shared by the host'}
          </p>
          <Button onClick={() => navigate('/live-classes')} variant="outline"
            className="rounded-xl border-[#252d3d] text-slate-300" data-testid="back-to-classes-btn">
            Back to Live Sessions
          </Button>
        </div>
      </div>
    );
  }

  // External meeting link view
  if (joinInfo?.meeting_type === 'external') {
    return (
      <div className="min-h-screen bg-[#0f1219] flex items-center justify-center p-4">
        <div className="bg-[#1a2033] rounded-2xl p-8 text-center max-w-md border border-[#252d3d]" data-testid="external-meeting-view">
          <div className="w-16 h-16 bg-pink-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ArrowSquareOut size={32} className="text-pink-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-1">{classInfo?.title}</h2>
          <p className="text-slate-400 text-sm mb-1">{classInfo?.instructor && `by ${classInfo.instructor}`}</p>
          <p className="text-slate-500 text-xs mb-6 flex items-center justify-center gap-2">
            <Clock size={14} /> {classInfo?.date} | {classInfo?.start_time} - {classInfo?.end_time}
          </p>
          <p className="text-slate-400 text-xs mb-4">This class uses an external meeting platform</p>
          <a href={joinInfo.external_meeting_link} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-pink-500 hover:bg-pink-600 text-white rounded-xl font-semibold text-sm transition-colors"
            data-testid="join-external-btn">
            <ArrowSquareOut size={18} /> Join Meeting
          </a>
          <div className="mt-4 flex items-center justify-center gap-2">
            <button onClick={copyMeetingLink} className="text-xs text-slate-500 hover:text-pink-400 flex items-center gap-1" data-testid="copy-meeting-link">
              <Copy size={14} /> Copy Link
            </button>
          </div>
          <div className="mt-6 pt-4 border-t border-[#252d3d]">
            <button onClick={() => navigate('/live-classes')} className="text-xs text-slate-500 hover:text-white">
              Back to Classes
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Jitsi embedded video room
  return (
    <div className="h-screen bg-[#0f1219] flex flex-col">
      {/* Top bar */}
      <div className="h-12 bg-[#1a2033] border-b border-[#252d3d] flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3">
          <HandHeart size={20} className="text-pink-500" weight="fill" />
          <div>
            <h3 className="text-white text-sm font-semibold">{classInfo?.title}</h3>
            <p className="text-[10px] text-slate-400">{classInfo?.instructor} | {classInfo?.session_type}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <Users size={14} /> {classInfo?.booked_count}/{classInfo?.max_participants}
          </span>
          <button onClick={copyMeetingLink} className="text-xs text-slate-400 hover:text-pink-400 flex items-center gap-1" data-testid="copy-room-link">
            <Copy size={14} /> Copy Link
          </button>
          <Button size="sm" onClick={handleLeave}
            className="bg-red-500 hover:bg-red-600 rounded-lg text-xs h-7 px-3" data-testid="leave-class-btn">
            <PhoneDisconnect size={14} className="mr-1" /> Leave
          </Button>
        </div>
      </div>

      {/* Jitsi container — host's camera filter applied via CSS */}
      <div ref={jitsiContainer}
        className={`flex-1 ${isHost && studio?.camera_filter === 'soft' ? 'studio-filter-soft' : ''} ${isHost && studio?.camera_filter === 'studio' ? 'studio-filter-strong' : ''}`}
        data-testid="jitsi-container" />
      {isHost && studio?.camera_filter && studio.camera_filter !== 'off' && (
        <style>{`
          .studio-filter-soft iframe { filter: saturate(1.08) brightness(1.04) contrast(1.02); }
          .studio-filter-strong iframe { filter: saturate(1.18) brightness(1.08) contrast(1.05) blur(0.2px); }
        `}</style>
      )}
    </div>
  );
};

export default VideoRoom;
